import React, { useEffect, useId, useRef, useState } from 'react';
import { FileText, Loader2, MessageSquareText, Paperclip, Send, Sparkles, UserPlus, X, Zap } from 'lucide-react';
import { S3Avatar, S3Menu, S3MenuItem } from './primitives';
import {
  activeMentions,
  aiMenuGroups,
  composerHint,
  composerModeLabel,
  composerPlaceholder,
  DRAFT_REPLY_NOTICE,
  filterMentionables,
  findMentionQuery,
  insertMention,
  INVITE_MENTION_LABEL,
  isDraftReplyMode,
  isReplyBlocked,
  mentionMenuEntries,
  mentionNotice,
  mentionToken,
  sendButtonLabel,
  TRIAGE_REPLY_NOTICE,
  type AiMenuItem,
  type MentionMenuEntry,
} from './composerModel';
import { useSupportLook } from '../v4/lookContext';
import { AI_ACTIONS, FILE_ACCEPT, formatFileSize, initialsOf, type ComposeTarget } from '../constants';
import { photoUrl } from '../../../utils/photoUrl';
import type { Macro, Mentionable, TicketDetail } from '../../../services/supportApi';

interface ComposerProps {
  ticket: TicketDetail;
  mode: ComposeTarget;
  onModeChange: (m: ComposeTarget) => void;
  draft: string;
  onDraftChange: (v: string) => void;
  files: File[];
  onFilesChange: (f: File[]) => void;
  /**
   * Riceve i file scelti gia copiati in un array: whitelist, 5 file, 10MB e i
   * relativi toast stanno nel contenitore (`TicketDetail.tsx:177-198`).
   */
  onPickFiles: (picked: File[]) => void;
  macros: Macro[];
  /** La sostituzione di {nominativo}{ticket}{operatore}{canale} sta nel contenitore. */
  onPickMacro: (m: Macro) => void;
  aiUnavailable: boolean;
  /** Richiesta AI in corso: spinner sulla pill e menu non riapribile. */
  aiLoading?: boolean;
  onAi: (action: string) => void;
  /**
   * Task 28: «Proponi una risposta» sull'intero ticket (solo in modalita
   * risposta e su ticket non chiuso). Assente = voce nascosta.
   */
  onAiSuggest?: () => void;
  /** Task 28: «Riassumi il ticket», proposto nella nota interna. Assente = voce nascosta. */
  onAiSummary?: () => void;
  /** Titolo del riquadro della proposta (default «Proposta dell'assistente»). */
  aiProposalLabel?: string;
  /** Testo proposto dall'AI; mai applicato da solo (ruling 7). */
  aiProposal: string | null;
  /** Task 29: contenuto sotto la proposta («Casi simili usati»). */
  aiProposalFooter?: React.ReactNode;
  onAiAccept: () => void;
  onAiCancel: () => void;
  /** Lo stato dopo la risposta si sceglie nel popup di conferma del contenitore. */
  onSend: () => void;
  busy: boolean;
  onTypingSignal?: (target: ComposeTarget) => void;
  onTypingStop?: (target: ComposeTarget) => void;
  /**
   * @menzioni (Task 48), solo in Nota interna. `mentionables`: chi si puo
   * menzionare (caricato dal contenitore la prima volta che si entra in nota;
   * assente o vuoto = niente elenco). `mentions`: menzioni scelte, mostrate
   * come chip; il contenitore le invia con la nota (solo quelle ancora nel testo).
   */
  mentionables?: Mentionable[] | null;
  mentions?: Mentionable[];
  onMentionsChange?: (m: Mentionable[]) => void;
  /**
   * Parte B: voce «Invita rappresentanti o esterni…» in coda all'elenco @.
   * Il contenitore apre il popup di ricerca e chiama `pick` con la persona
   * scelta, che entra nel testo come menzione al posto della `@…` digitata.
   * Assente = nessuna voce (chi non può invitare).
   */
  onInvite?: (pick: (m: Mentionable) => void) => void;
  /**
   * Canale di smistamento (`ticket.replyBlockedReason === 'da_smistare'`):
   * bottone «Trasferisci a un gruppo» dell'avviso al posto della risposta.
   * Assente = avviso senza bottone (chi non puo trasferire).
   */
  onTransfer?: () => void;
}

/** Elenco dei menzionabili: sopra la casella, scorrevole oltre ~6 voci. */
const MENTION_POP_STYLE: React.CSSProperties = {
  left: 8, bottom: 'calc(100% + 4px)', maxHeight: 236, overflowY: 'auto', width: 'min(300px, calc(100% - 16px))',
};
const MENTION_ACTIVE_STYLE: React.CSSProperties = { background: 'var(--a2-subtle)' };
const MENTION_ROLE_STYLE: React.CSSProperties = { marginLeft: 'auto', flex: 'none' };
const MENTION_NOTICE_STYLE: React.CSSProperties = {
  alignSelf: 'center', font: "500 11px 'DM Sans', sans-serif", color: 'var(--a2-fnt)',
};

/** Inattivita oltre la quale si smette di dichiararsi "sta scrivendo". */
const TYPING_IDLE_MS = 1000;

/**
 * Composer a due modalita (prototipo L254-L302, SPEC §3.11): una sola casella
 * di scrittura che passa da "Risposta all'associato" a "Nota interna" virando
 * ad ambra, al posto dei due composer separati di oggi.
 *
 * Non chiama l'API: invio, validazione degli allegati, macro e chiamata AI
 * stanno nel contenitore (Task 10). Due scelte non negoziabili:
 * - ruling 7: la proposta AI non sovrascrive mai la bozza, si accetta con "Usa";
 * - ruling 11: lo stato dopo l'invio si sceglie nel popup di conferma del
 *   contenitore (solo transizioni valide, nessuna preselezionata), non qui.
 */
const Composer: React.FC<ComposerProps> = ({
  ticket,
  mode,
  onModeChange,
  draft,
  onDraftChange,
  files,
  onFilesChange,
  onPickFiles,
  macros,
  onPickMacro,
  aiUnavailable,
  aiLoading = false,
  onAi,
  onAiSuggest,
  onAiSummary,
  aiProposalLabel = "Proposta dell'assistente",
  aiProposal,
  aiProposalFooter,
  onAiAccept,
  onAiCancel,
  onSend,
  busy,
  onTypingSignal,
  onTypingStop,
  mentionables,
  mentions = [],
  onMentionsChange,
  onInvite,
  onTransfer,
}) => {
  const v4 = useSupportLook() === 'v4';
  const menuId = useId();
  const [quickOpen, setQuickOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const quickRef = useRef<HTMLButtonElement>(null);
  const aiRef = useRef<HTMLButtonElement>(null);
  const isNote = mode === 'note';
  // Task 46: componente senza «puo rispondere» → la risposta diventa una bozza.
  const draftReply = isDraftReplyMode(mode, ticket);
  // Canale di smistamento (13/10/2026): in modalita risposta niente casella,
  // risposte rapide, proposta o rifinitura AI (resta «Riassumi il ticket»).
  // In Nota interna tutto come sempre (risposte rapide e rifinitura comprese, CT-85).
  const replyBlocked = isReplyBlocked(ticket);
  const blockedReply = replyBlocked && !isNote;
  const empty = !draft.trim() && files.length === 0;
  const canSuggest = Boolean(onAiSuggest) && !isNote && ticket.status !== 'chiuso' && !replyBlocked;
  const canSummarize = Boolean(onAiSummary);

  // ===== @menzioni (Task 48): solo in Nota interna =====
  // Parte B: chi puo invitare vede l'elenco anche senza menzionabili (solo la voce «Invita…»).
  const canInvite = isNote && Boolean(onInvite) && Boolean(onMentionsChange);
  const canMention = isNote && Boolean(onMentionsChange) && (Boolean(mentionables && mentionables.length) || canInvite);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [mentionQuery, setMentionQuery] = useState<{ start: number; query: string } | null>(null);
  const [mentionIdx, setMentionIdx] = useState(0);
  /** Cursore da ripristinare dopo l'inserimento di una menzione. */
  const pendingCaret = useRef<number | null>(null);
  const mentionEntries: MentionMenuEntry<Mentionable>[] =
    canMention && mentionQuery
      ? mentionMenuEntries(filterMentionables(mentionables || [], mentionQuery.query, mentions), canInvite)
      : [];
  const mentionOpen = mentionEntries.length > 0;
  const activeIdx = Math.min(mentionIdx, Math.max(0, mentionEntries.length - 1));
  const listboxId = `${menuId}-mentions`;
  const optionId = (i: number) => `${menuId}-mention-${i}`;

  const readMentionQuery = (text: string, caret: number | null) => {
    if (!canMention) return;
    setMentionQuery(findMentionQuery(text, caret ?? text.length));
    setMentionIdx(0);
  };

  // Clic fuori dall'elenco (e fuori dalla casella): l'elenco si chiude.
  useEffect(() => {
    if (!mentionOpen) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || taRef.current?.contains(target)) return;
      setMentionQuery(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [mentionOpen]);

  // Uscendo dalla nota l'elenco non deve restare in sospeso.
  useEffect(() => {
    if (!isNote) setMentionQuery(null);
  }, [isNote]);

  // Dopo l'inserimento il cursore va subito dopo «@Nome Cognome ».
  useEffect(() => {
    const ta = taRef.current;
    if (pendingCaret.current === null || !ta) return;
    const pos = Math.min(pendingCaret.current, ta.value.length);
    pendingCaret.current = null;
    ta.focus();
    ta.setSelectionRange(pos, pos);
  }, [draft]);

  const pickMention = (m: Mentionable) => {
    if (!mentionQuery || !onMentionsChange) return;
    const caret = mentionQuery.start + 1 + mentionQuery.query.length;
    const next = insertMention(draft, mentionQuery.start, caret, m.nome);
    pendingCaret.current = next.caret;
    setMentionQuery(null);
    onDraftChange(next.text);
    onMentionsChange([...mentions, m]);
  };

  /**
   * «Invita…»: chiude l'elenco e passa al contenitore una funzione che, scelta
   * la persona nel popup, la inserisce come menzione dove era la `@…`. Testo e
   * menzioni sono quelli di adesso: col popup aperto non si scrive.
   */
  const openInvite = () => {
    if (!mentionQuery || !onInvite || !onMentionsChange) return;
    const q = mentionQuery;
    const text = draft;
    const current = mentions;
    setMentionQuery(null);
    onInvite((m) => {
      const next = insertMention(text, q.start, q.start + 1 + q.query.length, m.nome);
      pendingCaret.current = next.caret;
      onDraftChange(next.text);
      onMentionsChange([...current.filter((x) => !(x.actorType === m.actorType && String(x.actorId) === String(m.actorId))), m]);
    });
  };

  const pickEntry = (en: MentionMenuEntry<Mentionable>) => (en.kind === 'invite' ? openInvite() : pickMention(en.item));

  /** Frecce, Invio/Tab ed Esc mentre l'elenco e aperto. true = tasto gestito. */
  const mentionKey = (e: React.KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!mentionOpen) return false;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const n = mentionEntries.length;
      setMentionIdx((activeIdx + (e.key === 'ArrowDown' ? 1 : n - 1)) % n);
    } else if ((e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) || e.key === 'Tab') {
      pickEntry(mentionEntries[activeIdx]);
    } else if (e.key === 'Escape') {
      // Esc chiude solo l'elenco, non il cassetto o la modale che lo contiene.
      e.stopPropagation();
      setMentionQuery(null);
    } else return false;
    e.preventDefault();
    return true;
  };

  // Timer del "sta scrivendo": il segnale parte a ogni battuta, lo stop dopo un
  // secondo di silenzio. Il contenitore ha gia la stessa finestra
  // (`AssociativeSupportDetail.tsx:165-187`) e la duplicazione e voluta: lo
  // stop e idempotente (emette solo se il flag "sta scrivendo" e alzato),
  // quindi il primo dei due timer che scade chiude e l'altro e un no-op. Serve
  // perche il composer sa cose che il contenitore non sa — blur, invio, cambio
  // di modalita, smontaggio — e in quei casi deve chiudere subito.
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTypingTimer = () => {
    if (typingTimer.current) {
      clearTimeout(typingTimer.current);
      typingTimer.current = null;
    }
  };
  // Smontaggio (cambio ticket compreso): il timer non deve sopravvivere.
  useEffect(() => clearTypingTimer, []);

  const stopTyping = (target: ComposeTarget) => {
    clearTypingTimer();
    onTypingStop?.(target);
  };

  const signalTyping = (target: ComposeTarget) => {
    onTypingSignal?.(target);
    clearTypingTimer();
    typingTimer.current = setTimeout(() => {
      typingTimer.current = null;
      onTypingStop?.(target);
    }, TYPING_IDLE_MS);
  };

  // L'invio vero (e la validazione della bozza vuota) sta nel contenitore:
  // qui si chiude solo la dichiarazione di scrittura in corso.
  const send = () => {
    stopTyping(mode);
    onSend();
  };

  const pickMode = (next: ComposeTarget) => {
    if (next === mode) return;
    stopTyping(mode);
    setQuickOpen(false);
    setAiOpen(false);
    onModeChange(next);
  };

  const aiGroups = aiMenuGroups({ canSuggest, canSummarize, draftActions: blockedReply ? [] : AI_ACTIONS });

  const runAiItem = (item: AiMenuItem) => {
    setAiOpen(false);
    if (item.kind === 'suggest') onAiSuggest?.();
    else if (item.kind === 'summary') onAiSummary?.();
    else onAi(item.key);
  };

  const aiItem = (item: AiMenuItem) => (
    <S3MenuItem key={item.key} onClick={() => runAiItem(item)}>
      {item.kind === 'suggest' && <MessageSquareText size={13} />}
      {item.kind === 'summary' && <FileText size={13} />}
      {item.kind === 'draft' ? item.label : ` ${item.label}`}
    </S3MenuItem>
  );

  // v3: azioni sull'intero ticket (Task 28) prima di quelle sulla bozza, con
  // un separatore. v4: le stesse voci nello stesso ordine, divise in due
  // gruppi con intestazione «Sul ticket» / «Sulla bozza» (L483-L488).
  const aiMenuContent = aiGroups.map((g, i) => (
    <React.Fragment key={g.id}>
      {i > 0 && <div role="separator" className="s3-menusep" />}
      {v4 ? (
        <div
          role="group"
          aria-labelledby={`${menuId}-${g.id}`}
          className={`s4-aimenu__group s4-aimenu__group--${g.id}`}
        >
          <div id={`${menuId}-${g.id}`} className="s4-aimenu__head">
            {g.label}
          </div>
          <div className="s4-aimenu__items">{g.items.map(aiItem)}</div>
        </div>
      ) : (
        g.items.map(aiItem)
      )}
    </React.Fragment>
  ));

  const tools = (
    /* Riga strumenti (L256-L281; v4 L463-L491) */
    <div className="s3-comp__tools">
      {/* Due bottoni `aria-pressed`, entrambi raggiungibili con Tab: stesso
          pattern dei segmented di SupportQueue e ThreadPanel. Un radiogroup
          vorrebbe la navigazione con le frecce, che qui non c'e. */}
      <div className="s3-seg" role="group" aria-label="Modalità di scrittura">
        {(['reply', 'note'] as ComposeTarget[]).map((m) => {
          const on = m === mode;
          return (
            <button
              key={m}
              type="button"
              aria-pressed={on}
              className={`s3-segbtn s3-segbtn--sm${on ? (m === 'note' ? ' s3-segbtn--amber' : ' s3-segbtn--on') : ''}`}
              onClick={() => pickMode(m)}
            >
              {composerModeLabel(m)}
            </button>
          );
        })}
      </div>

      <span className="s3-comp__spacer" />

      {/* Risposte rapide: solo se il gruppo ha macro (parita ThreadComposer.tsx:186).
          Restano anche in Nota interna (CT-85): il design v4 le mostra solo in
          risposta (L469), ma la funzione di oggi vince. */}
      {macros.length > 0 && !blockedReply && (
        <div className="s3-comp__anchor">
          <button
            ref={quickRef}
            type="button"
            className={`s3-pill s3-pill--sm${quickOpen ? ' s3-pill--active' : ''}`}
            aria-haspopup="menu"
            aria-expanded={quickOpen}
            onClick={() => {
              setQuickOpen((v) => !v);
              setAiOpen(false);
            }}
          >
            <Zap size={12} /> Risposte rapide
          </button>
          <S3Menu
            open={quickOpen}
            onClose={() => setQuickOpen(false)}
            anchor="up"
            ariaLabel="Risposte rapide"
            triggerRef={quickRef}
          >
            {macros.map((m) => (
              <S3MenuItem
                key={m._id}
                onClick={() => {
                  setQuickOpen(false);
                  onPickMacro(m);
                }}
              >
                <span className="s3-ellipsis">{m.titolo}</span>
              </S3MenuItem>
            ))}
          </S3Menu>
        </div>
      )}

      {/* Un solo bottone AI: le azioni stanno nel menu (ruling 7). Senza AI
          (chiave assente / 503) il bottone non c'e (CT-93). */}
      {!aiUnavailable && aiGroups.length > 0 && (
        <div className="s3-comp__anchor">
          <button
            ref={aiRef}
            type="button"
            className="s3-pill s3-pill--sm s3-pill--vio"
            aria-haspopup="menu"
            aria-expanded={aiOpen}
            aria-busy={aiLoading}
            disabled={aiLoading}
            onClick={() => {
              setAiOpen((v) => !v);
              setQuickOpen(false);
            }}
          >
            {aiLoading ? <Loader2 size={12} className="s3-spin" /> : <Sparkles size={12} />}{' '}
            Assistente AI
          </button>
          <S3Menu
            open={aiOpen}
            onClose={() => setAiOpen(false)}
            anchor="up"
            ariaLabel="Azioni dell'assistente"
            triggerRef={aiRef}
          >
            {aiMenuContent}
          </S3Menu>
        </div>
      )}
    </div>
  );

  /* Proposta AI: mai applicata da sola (ruling 7). La regione vive anche
     vuota, altrimenti l'annuncio del testo appena arrivato si perde. */
  const proposalLabel = (
    <span className="s3-label s3-ai__label">
      <Sparkles size={11} /> {aiProposalLabel}
    </span>
  );
  const proposal = (
    <div role="status" aria-live="polite">
      {aiProposal !== null && (
        <div className="s3-ai">
          {v4 ? (
            // v4 (L449): accanto al titolo la dicitura che la proposta non parte da sola.
            <div className="s4-ai__head">
              {proposalLabel}
              <span className="s4-ai__notice">Non viene inviata da sola</span>
            </div>
          ) : (
            proposalLabel
          )}
          <div className="s3-ai__text">{aiProposal}</div>
          {aiProposalFooter}
          <div className="s3-ai__actions">
            <button type="button" className="s3-pill s3-pill--sm" onClick={onAiCancel}>
              Annulla
            </button>
            <button
              type="button"
              className="s3-pill s3-pill--sm s3-pill--primary"
              onClick={onAiAccept}
            >
              Usa
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const textareaEl = (
    <textarea
      ref={taRef}
      className={`s3-ta${isNote ? ' s3-ta--note' : ''}`}
      rows={3}
      value={draft}
      placeholder={composerPlaceholder(mode)}
      aria-label={composerModeLabel(mode)}
      {...(canMention
        ? {
            'aria-autocomplete': 'list' as const,
            'aria-expanded': mentionOpen,
            'aria-controls': mentionOpen ? listboxId : undefined,
            'aria-activedescendant': mentionOpen ? optionId(activeIdx) : undefined,
          }
        : {})}
      onChange={(e) => {
        const value = e.target.value;
        onDraftChange(value);
        signalTyping(mode);
        readMentionQuery(value, e.target.selectionStart);
        // Cancellare «@Nome» dal testo toglie anche la menzione.
        if (onMentionsChange && mentions.length) {
          const still = activeMentions(value, mentions);
          if (still.length !== mentions.length) onMentionsChange(still);
        }
      }}
      onClick={(e) => readMentionQuery(e.currentTarget.value, e.currentTarget.selectionStart)}
      onBlur={() => stopTyping(mode)}
      onKeyDown={(e) => {
        if (mentionKey(e)) return;
        if (!isNote && (e.ctrlKey || e.metaKey) && e.key === 'Enter') {
          e.preventDefault();
          send();
        }
      }}
    />
  );

  const mentionPopover = mentionOpen ? (
    <div
      ref={popRef}
      id={listboxId}
      role="listbox"
      aria-label="Colleghi da menzionare"
      className="s3-menu s4-mentionpop"
      style={MENTION_POP_STYLE}
    >
      {mentionEntries.map((en, i) =>
        en.kind === 'invite' ? (
          <div
            key="invite"
            id={optionId(i)}
            role="option"
            aria-selected={i === activeIdx}
            className="s3-menuitem"
            style={i === activeIdx ? MENTION_ACTIVE_STYLE : undefined}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setMentionIdx(i)}
            onClick={openInvite}
          >
            <UserPlus size={14} aria-hidden="true" />
            <span className="s3-ellipsis">{INVITE_MENTION_LABEL}</span>
          </div>
        ) : (
          <div
            key={`${en.item.actorType}-${en.item.actorId}`}
            id={optionId(i)}
            role="option"
            aria-selected={i === activeIdx}
            className="s3-menuitem"
            style={i === activeIdx ? MENTION_ACTIVE_STYLE : undefined}
            // Il fuoco resta nella casella: la scelta avviene al clic.
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => setMentionIdx(i)}
            onClick={() => pickMention(en.item)}
          >
            <S3Avatar initials={initialsOf(en.item.nome)} size={22} tone="chip" src={photoUrl(en.item.foto)} />
            <span className="s3-ellipsis">{en.item.nome}</span>
            <span className="s3-meta" style={MENTION_ROLE_STYLE}>
              {en.item.ruolo}
            </span>
          </div>
        )
      )}
    </div>
  ) : null;

  // L'elenco si ancora alla casella: serve un contenitore posizionato.
  const textarea = canMention ? (
    <div style={{ position: 'relative' }}>
      {textareaEl}
      {mentionPopover}
    </div>
  ) : (
    textareaEl
  );

  const mentionChips =
    isNote && mentions.length > 0 ? (
      <div className="s3-comp__files" style={{ alignItems: 'center' }}>
        <ul aria-label="Colleghi menzionati" style={{ display: 'contents', listStyle: 'none' }}>
          {mentions.map((m) => (
            <li key={`${m.actorType}-${m.actorId}`}>
              <button
                type="button"
                className="s3-pill s3-pill--sm"
                onClick={() => onMentionsChange?.(mentions.filter((x) => x !== m))}
                aria-label={`Togli la menzione di ${m.nome}`}
              >
                <span className="s3-ellipsis">{mentionToken(m.nome)}</span>
                <X size={11} />
              </button>
            </li>
          ))}
        </ul>
        <span style={MENTION_NOTICE_STYLE}>{mentionNotice(mentions.length)}</span>
      </div>
    ) : null;

  const fileList =
    files.length > 0 ? (
      <ul className="s3-comp__files">
        {files.map((f, i) => (
          <li key={`${f.name}-${i}`}>
            <button
              type="button"
              className="s3-pill s3-pill--sm"
              onClick={() => onFilesChange(files.filter((_, j) => j !== i))}
              aria-label={`Rimuovi ${f.name}`}
            >
              <span className="s3-ellipsis">
                {f.name} · {formatFileSize(f.size)}
              </span>
              <X size={11} />
            </button>
          </li>
        ))}
      </ul>
    ) : null;

  const sendRow = (
    /* Riga invio (L283-L300; v4 L500-L507) */
    <div className="s3-comp__send">
      <label className="s3-pill s3-pill--sm s3-comp__attach">
        <Paperclip size={12} /> Allega
        {/* Fuori schermo ma NON `hidden`: cosi resta raggiungibile con Tab e
            apribile con Invio, come il bottone che sembra. */}
        <input
          type="file"
          multiple
          className="s3-sr"
          accept={FILE_ACCEPT}
          aria-label="Allega file"
          onChange={(e) => {
            const input = e.target;
            const picked = Array.from(input.files || []);
            input.value = ''; // consente di riselezionare lo stesso file
            if (picked.length > 0) onPickFiles(picked);
          }}
        />
      </label>

      <span className="s3-meta s3-comp__hint">{composerHint(mode)}</span>

      <span className="s3-comp__spacer" />

      {/* Niente select "Invia e …" accanto al tasto (25/10/2026): lo stato
          dopo la risposta si sceglie, obbligatoriamente, nel popup di conferma. */}

      {/* Bozza vuota: NON `disabled`, il click porta al toast di validazione
          del contenitore (L662). `busy` invece disabilita davvero. */}
      <button
        type="button"
        className={`s3-pill s3-comp__send-btn${empty ? ' s3-pill--muted' : isNote ? ' s3-pill--amber' : ' s3-pill--primary'}`}
        disabled={busy}
        onClick={send}
      >
        <Send size={12} strokeWidth={2.5} /> {sendButtonLabel(mode, draftReply)}
      </button>
    </div>
  );

  // Task 46: la risposta parte solo dopo l'approvazione del responsabile.
  // Stile in linea (support-v3.css non si tocca): token ambra, font piccolo.
  const draftNotice = draftReply ? (
    <p
      role="note"
      style={{
        margin: 0,
        padding: '6px 10px',
        borderRadius: 9,
        border: '1px dashed var(--a2-warn-bd)',
        background: 'var(--a2-warn-bg)',
        color: 'var(--a2-warn-fg)',
        font: "500 11.5px/1.45 'DM Sans', sans-serif",
      }}
    >
      {DRAFT_REPLY_NOTICE}
    </p>
  ) : null;

  // Canale di smistamento: al posto della casella di risposta un avviso con il
  // trasferimento (stesso pannello/modale del menu ⋯). Stile in linea come
  // l'avviso delle bozze (support-v3.css non si tocca), font piccolo.
  const triageNotice = blockedReply ? (
    <div
      role="note"
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        margin: 0,
        padding: '8px 10px',
        borderRadius: 9,
        border: '1px dashed var(--a2-warn-bd)',
        background: 'var(--a2-warn-bg)',
        color: 'var(--a2-warn-fg)',
        font: "500 11.5px/1.45 'DM Sans', sans-serif",
      }}
    >
      <span style={{ flex: '1 1 220px' }}>{TRIAGE_REPLY_NOTICE}</span>
      {onTransfer && (
        <button type="button" className="s3-pill s3-pill--sm s3-pill--primary" disabled={busy} onClick={onTransfer}>
          Trasferisci a un gruppo
        </button>
      )}
    </div>
  ) : null;

  if (blockedReply) {
    return v4 ? (
      <div className="s3-comp s4-comp">
        <div className="s4-comp__box">
          {tools}
          {triageNotice}
        </div>
      </div>
    ) : (
      <div className="s3-comp">
        {tools}
        {triageNotice}
      </div>
    );
  }

  if (v4) {
    // v4 (L445-L509): la proposta sta sopra; strumenti, testo, allegati e
    // invio vivono in un unico riquadro il cui bordo vira in ambra in nota.
    return (
      <div className={`s3-comp s4-comp${isNote ? ' s3-comp--note' : ''}`}>
        {proposal}
        <div className="s4-comp__box">
          {tools}
          {textarea}
          {fileList}
          {mentionChips}
          {draftNotice}
          {sendRow}
        </div>
      </div>
    );
  }

  return (
    <div className={`s3-comp${isNote ? ' s3-comp--note' : ''}`}>
      {tools}
      {proposal}
      {textarea}
      {fileList}
      {mentionChips}
      {draftNotice}
      {sendRow}
    </div>
  );
};

export default Composer;
